import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const models = [
  { icon: "🎟️", en: "Paid Content & Membership", zh: "会员付费与内容解锁", desc: "Users pay to unlock premium episodes and exclusive content.", tag: "Core" },
  { icon: "📣", en: "Advertising & Brand Integration", zh: "广告与品牌植入", desc: "Brands embedded into storylines for enhanced engagement and monetization.", tag: "B2B" },
  { icon: "🤝", en: "Revenue Sharing with Partners", zh: "投资分账模式", desc: "Structured profit-sharing model for content production partnerships.", tag: "B2B" },
  { icon: "🏆", en: "IP Licensing & Derivatives", zh: "IP授权与衍生开发", desc: "Expanding successful stories into long-term intellectual property assets.", tag: "Growth" },
  { icon: "🌐", en: "Platform Ecosystem", zh: "平台化变现", desc: "Future revenue from proprietary platform ecosystem and growing user base.", tag: "Future" },
];

const tagColors: Record<string, string> = {
  Core: "#FF1C35",
  B2B: "#7bc4e0",
  Growth: "#c8a96e",
  Future: "#82c996",
};

const BusinessModel = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="business" ref={ref} style={{
      padding: "100px 40px",
      background: "linear-gradient(180deg, #080808 0%, rgba(12,12,20,0.9) 100%)",
      borderTop: "1px solid rgba(255,255,255,0.05)",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>BUSINESS MODEL · 商业模式</span>
          </div>
          <div style={{ marginBottom: 56, maxWidth: 620 }}>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 900, lineHeight: 1.1, margin: "0 0 16px" }}>
              Diversified<br /><span style={{ color: "#FF1C35" }}>Monetization Model</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 15, lineHeight: 1.7, margin: 0 }}>
              Multiple revenue streams maximizing value across the entire content lifecycle.<br />
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.25)" }}>短期现金流 + 长期IP价值 = 可持续可扩展的商业体系</span>
            </p>
          </div>
        </motion.div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {models.map((m, i) => (
            <motion.div key={m.en}
              initial={{ opacity: 0, y: 24 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              whileHover={{ borderColor: `${tagColors[m.tag]}40` } as never}
              style={{
                padding: "28px 24px", borderRadius: 16,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)",
                transition: "all 0.3s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <span style={{ fontSize: 32 }}>{m.icon}</span>
                <span style={{
                  padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: `${tagColors[m.tag]}18`, color: tagColors[m.tag],
                  border: `1px solid ${tagColors[m.tag]}30`,
                }}>{m.tag}</span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{m.en}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 10 }}>{m.zh}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.7 }}>{m.desc}</div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={visible ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}
          style={{
            marginTop: 40, padding: "20px 28px", borderRadius: 12,
            background: "rgba(255,28,53,0.05)", border: "1px solid rgba(255,28,53,0.12)",
            textAlign: "center",
          }}>
          <p style={{ margin: 0, fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
            <strong style={{ color: "#FF1C35" }}>Short-term revenue + Long-term IP value</strong> = Sustainable & scalable business model
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default BusinessModel;
